import React, { createContext, Dispatch, PropsWithChildren, useContext, useEffect, useReducer, useRef } from 'react';
import {
    getMaintenanceMenus,
    getRNDServices,
    getSpgPricing,
    getAllServices,
    IService,
    ISpgPricingData,
    MaintenanceMenu,
    MaintenanceMenuData,
    RNDService,
    RNDServiceData,
} from '../API/GoMoto';
import { MaintenanceMenuType } from '../API/Enums';
import { useAppointmentStateUnsafe } from './Appointment/appointment';
import { useKioskState } from './Kiosk/kiosk';
import { DisplayMake, DisplayModel } from '../helpers/displayVehicle';
import { ActionMap } from '../API/GoMoto.types';

export enum BackgroundDispatchTypes {
    // ALL AND Standard Services
    SetStandardServices = 'SET_STANDARD_SERVICES',
    SetAllServices = 'SET_ALL_SERVICES',
    ClearServices = 'CLEAR_SERVICES',

    // Maintenance Menus
    SetLoadingMenus = 'SET_LOADING_MENUS',
    SetMenuAPIData = 'SET_MENU_API_DATA',
    SetMenus = 'SET_MENUS',
    ClearMenus = 'CLEAR_MENUS',
    ReinitializeMenus = 'REINITIALIZE_MENUS',
    SetSelectedScheduleOption = 'SET_SELECTED_SCHEDULE',
    SetSelectedMileageOption = 'SET_SELECTED_MILEAGE',
    SetPastMileagePage = 'SET_PAST_MILEAGE_PAGE',

    // RND Services
    SetLoadingRNDServices = 'SET_LOADING_RND_SERVICES',
    SetRNDServices = 'SET_RND_SERVICES',
    ClearRNDServices = 'CLEAR_RND_SERVICES',
    ReinitializeRNDServices = 'REINITIALIZE_RND_SERVICES',
    SetRNDServicesInitialized = 'SET_RND_SERVICES_INITIALIZED',
    InitializeRNDServices = 'INITIALIZE_RND_SERVICES'
}

type BackgroundPayload = {
    // Standard Services
    [BackgroundDispatchTypes.ClearServices]: never;
    [BackgroundDispatchTypes.SetStandardServices]: IService[];
    [BackgroundDispatchTypes.SetAllServices]: IService[];

    // Maintenance Menus
    [BackgroundDispatchTypes.ClearMenus]: never;
    [BackgroundDispatchTypes.SetLoadingMenus]: never;
    [BackgroundDispatchTypes.SetMenuAPIData]: MaintenanceMenuData;
    [BackgroundDispatchTypes.ReinitializeMenus]: never;
    [BackgroundDispatchTypes.SetMenus]: MaintenanceMenu[];
    [BackgroundDispatchTypes.SetSelectedScheduleOption]: string;
    [BackgroundDispatchTypes.SetSelectedMileageOption]: string;
    [BackgroundDispatchTypes.SetPastMileagePage]: boolean;

    // RND Services
    [BackgroundDispatchTypes.ClearRNDServices]: never;
    [BackgroundDispatchTypes.SetRNDServices]: RNDServiceData;
    [BackgroundDispatchTypes.SetLoadingRNDServices]: never;
    [BackgroundDispatchTypes.ReinitializeRNDServices]: never;
    [BackgroundDispatchTypes.SetRNDServicesInitialized]: boolean;
    [BackgroundDispatchTypes.InitializeRNDServices]: RNDService[];
};

type BackgroundActions = ActionMap<BackgroundPayload>[keyof ActionMap<BackgroundPayload>];

interface BackgroundStateType extends MaintenanceMenuData {
    standardServices: IService[];
    allServices: IService[];
    standardServicesInitialized: boolean;
    menusInitialized: boolean;
    loadingMenus: boolean;
    selectedScheduleOption: string | null;
    selectedMileageOption: string | null;
    pastMileagePage: boolean;
    rndServices: RNDService[];
    rndLastVisit: string | null;
    loadingRndServices: boolean;
    rndServicesInitialized: boolean;
    rndServicesFetched: boolean;
}

const emptyMenuData: MaintenanceMenuData = {
    menus: [],
    defaultScheduleOption: null,
    defaultMileageOption: null,
    scheduleOptions: [],
    mileageOptions: [],
};

const initialState: BackgroundStateType = {
    ...emptyMenuData,
    standardServices: [],
    allServices: [],
    standardServicesInitialized: false,
    menusInitialized: false,
    loadingMenus: false,
    selectedScheduleOption: null,
    selectedMileageOption: null,
    pastMileagePage: false,
    rndServices: [],
    rndLastVisit: null,
    loadingRndServices: false,
    rndServicesInitialized: false,
    rndServicesFetched: false,
};

const backgroundReducer = (state: BackgroundStateType, action: BackgroundActions): BackgroundStateType => {
    switch (action.type) {
        case BackgroundDispatchTypes.SetLoadingMenus:
            return { ...state,
                loadingMenus: true };
        case BackgroundDispatchTypes.ClearServices:
            return { ...state,
                standardServices: [],
                standardServicesInitialized: false };
        case BackgroundDispatchTypes.ClearMenus:
            return { ...state,
                menus: [] };
        case BackgroundDispatchTypes.SetStandardServices:
            return { ...state,
                standardServices: action.payload,
                standardServicesInitialized: true };
        case BackgroundDispatchTypes.SetAllServices:
            return { ...state,
                allServices: action.payload };
        case BackgroundDispatchTypes.SetMenuAPIData:
            return {
                ...state,
                loadingMenus: false,
                menusInitialized: true,
                menus: action.payload.menus ?? [],
                defaultScheduleOption: action.payload.defaultScheduleOption,
                defaultMileageOption: action.payload.defaultMileageOption,
                scheduleOptions: action.payload.scheduleOptions ?? [],
                mileageOptions: action.payload.mileageOptions ?? [],
                selectedScheduleOption: state.selectedScheduleOption === null ? action.payload.defaultScheduleOption : state.selectedScheduleOption,
                selectedMileageOption: state.selectedMileageOption === null ? action.payload.defaultMileageOption : state.selectedMileageOption,
            };
        case BackgroundDispatchTypes.ReinitializeMenus:
            return {
                ...state,
                ...emptyMenuData,
                menusInitialized: false,
                selectedScheduleOption: null,
                selectedMileageOption: null,
            };
        case BackgroundDispatchTypes.SetMenus:
            return {
                ...state,
                loadingMenus: false,
                menus: action.payload,
            };
        case BackgroundDispatchTypes.SetSelectedScheduleOption:
            return { ...state,
                selectedScheduleOption: action.payload,
                selectedMileageOption: null };
        case BackgroundDispatchTypes.SetSelectedMileageOption:
            return { ...state,
                selectedMileageOption: action.payload };
        case BackgroundDispatchTypes.ClearRNDServices:
            return {
                ...state,
                rndServicesFetched: false,
                rndServices: [],
                rndLastVisit: null,
            };
        case BackgroundDispatchTypes.SetLoadingRNDServices:
            return { ...state,
                loadingRndServices: true };
        case BackgroundDispatchTypes.SetRNDServices:
            return {
                ...state,
                loadingRndServices: false,
                rndServicesFetched: true,
                rndServices: action.payload.services ?? [],
                rndLastVisit: action.payload.lastVisit,
            };
        case BackgroundDispatchTypes.ReinitializeRNDServices:
            return {
                ...state,
                rndServicesInitialized: false,
                rndServicesFetched: false,
                rndServices: [],
                rndLastVisit: null,
            };
        case BackgroundDispatchTypes.SetRNDServicesInitialized:
            return {
                ...state,
                rndServicesInitialized: action.payload,
            };
        case BackgroundDispatchTypes.InitializeRNDServices:
            return {
                ...state,
                rndServices: action.payload ?? [],
                rndServicesInitialized: true,
            };
        case BackgroundDispatchTypes.SetPastMileagePage:
            return { ...state,
                pastMileagePage: action.payload };
        default:
            return state;
    }
};

const BackgroundStateContext = createContext<BackgroundStateType>(initialState);
const BackgroundDispatchContext = createContext<Dispatch<BackgroundActions>>(() => null);

type MaintenanceMenuCache = { [key: string]: MaintenanceMenu[]; };

interface IBackgroundDataProviderProps {
    suppressBackgroundDataProvider?: boolean;
}

export const BackgroundDataProvider: React.FC<PropsWithChildren<IBackgroundDataProviderProps>> = (props) => {
    const [state, dispatch] = useReducer(backgroundReducer, initialState);
    const appointment = useAppointmentStateUnsafe();
    const {
        key: kioskKey,
        isMultiMake,
        dealer: { key: dealerKey, make: dealerMake, maintenanceMenusEnabled, xtimeEnabled, recommendedNotDoneEnabled },
    } = useKioskState();

    const vin = appointment?.vin ?? '';
    const mileage = appointment?.mileage ?? 0;
    const year = appointment?.year ?? '';
    const make = appointment && DisplayMake(appointment) !== '' ? DisplayMake(appointment) : isMultiMake ? dealerMake : '';
    const model = appointment ? DisplayModel(appointment) : '';
    const apptkey = appointment?.key ?? '';
    const keyStatus = appointment?.keyStatus ?? '';
    const canDispatch = !props.suppressBackgroundDataProvider;

    const maintenanceMenuCache = useRef<MaintenanceMenuCache>({});

    useEffect(() => {
        let isMounted: boolean = true;

        if (canDispatch) {
            dispatch({ type: BackgroundDispatchTypes.ClearServices });
            // Set a flag to update rnd services that are linked to GoMoto services with up to date info.
            dispatch({ type: BackgroundDispatchTypes.SetRNDServicesInitialized,
                payload: false });

            if (apptkey && ['', 'none'].includes(keyStatus)) {
                getAllServices(kioskKey, parseInt(year, 10), make)
                    .then((services: IService[]) => {
                        const opCodesWithSpg = services.filter((s) => !!s.useSpgPricing && !!s.spgPricing && !!s.opCode)
                            .map((s) => s.opCode!);
                        if (opCodesWithSpg.length !== 0 && state.pastMileagePage) {
                            getSpgPricing(kioskKey, dealerKey, opCodesWithSpg, vin, parseInt(year, 10), make, model, mileage)
                                .then(
                                    (spgPricingData: ISpgPricingData[]) => {
                                        spgPricingData.forEach((spg) => {
                                            if (spg.isSpgOp) {
                                                services.forEach((s, index) => {
                                                    if (s.opCode === spg.opCode) {
                                                        services[index] = { ...s,
                                                            ...spg };
                                                    }
                                                });
                                            }
                                        });

                                        isMounted && dispatch({ type: BackgroundDispatchTypes.SetAllServices,
                                            payload: services });
                                        isMounted && dispatch({ type: BackgroundDispatchTypes.SetStandardServices,
                                            payload: services.filter((s) => s.showInCart) });
                                    },
                                );
                        }
                        else {
                            isMounted && dispatch({ type: BackgroundDispatchTypes.SetAllServices,
                                payload: services });
                            isMounted && dispatch({ type: BackgroundDispatchTypes.SetStandardServices,
                                payload: services.filter((s) => s.showInCart) });
                        }
                    })
                    .catch(() => {
                        isMounted && dispatch({ type: BackgroundDispatchTypes.SetAllServices,
                            payload: [] });
                        isMounted && dispatch({ type: BackgroundDispatchTypes.SetStandardServices,
                            payload: [] });
                    });
            }
        }

        return () => {
            isMounted = false;
        };
    }, [kioskKey, dealerKey, vin, year, make, model, mileage, apptkey, keyStatus, state.pastMileagePage, canDispatch]);

    useEffect(() => {
        if (canDispatch) {
            dispatch({ type: BackgroundDispatchTypes.ReinitializeMenus });
            dispatch({ type: BackgroundDispatchTypes.ReinitializeRNDServices });
        }
    }, [kioskKey, dealerKey, vin, mileage, canDispatch]);

    useEffect(() => {
        if (!state.menusInitialized) {
            maintenanceMenuCache.current = {};
        }
    }, [state.menusInitialized]);

    useEffect(() => {
        let isMounted: boolean = true;

        if (canDispatch) {
            dispatch({ type: BackgroundDispatchTypes.ClearMenus });

            if (maintenanceMenusEnabled && !xtimeEnabled && vin.length > 0 && ['', 'none'].includes(keyStatus) && state.pastMileagePage) {
                dispatch({ type: BackgroundDispatchTypes.SetLoadingMenus });

                const mileageMenus: MaintenanceMenu[] | undefined =
                    state.selectedMileageOption != null && state.selectedScheduleOption !== null ?
                        // mileage options are determined by the selected schedule option, so the key is a pair of both
                        maintenanceMenuCache.current['MIL' + state.selectedScheduleOption + state.selectedMileageOption]
                        : undefined;
                const scheduleMenus: MaintenanceMenu[] | undefined =
                    state.selectedScheduleOption !== null ? maintenanceMenuCache.current['SCH' + state.selectedScheduleOption] : undefined;

                if (mileageMenus !== undefined && scheduleMenus !== undefined) {
                    isMounted && dispatch({ type: BackgroundDispatchTypes.SetMenus,
                        payload: scheduleMenus.concat(mileageMenus) });
                }
                else {
                    getMaintenanceMenus(kioskKey, dealerKey, vin, make, model, parseInt(year, 10), mileage, state.selectedScheduleOption, state.selectedMileageOption)
                        .then((menus: MaintenanceMenuData) => {
                            if (state.selectedScheduleOption || menus.defaultScheduleOption) {
                                maintenanceMenuCache.current['SCH' + (state.selectedScheduleOption ?? menus.defaultScheduleOption)] = menus.menus.filter(
                                    (m) => m.type === MaintenanceMenuType.Schedule,
                                );

                                if (state.selectedMileageOption !== null || menus.defaultMileageOption !== null) {
                                    // mileage options are determined by the selected schedule option, so the key is a pair of both
                                    maintenanceMenuCache.current[
                                        'MIL' +
                                        (state.selectedScheduleOption ?? menus.defaultScheduleOption) +
                                        (state.selectedMileageOption ?? menus.defaultMileageOption)
                                    ] = menus.menus.filter((m) => m.type === MaintenanceMenuType.Mileage);
                                }
                            }

                            isMounted && dispatch({ type: BackgroundDispatchTypes.SetMenuAPIData,
                                payload: menus });
                        })
                        .catch(() => {
                            isMounted && dispatch({ type: BackgroundDispatchTypes.SetMenuAPIData,
                                payload: emptyMenuData });
                        });

                }
            }
            else {
                // ensure the menusInitialized flag is set to true
                dispatch({ type: BackgroundDispatchTypes.SetMenuAPIData,
                    payload: emptyMenuData });
            }

        }

        return () => {
            isMounted = false;
        };
    }, [
        kioskKey,
        dealerKey,
        vin,
        make,
        model,
        year,
        mileage,
        maintenanceMenusEnabled,
        xtimeEnabled,
        state.selectedMileageOption,
        state.selectedScheduleOption,
        state.pastMileagePage,
        keyStatus,
        canDispatch,
    ]);

    useEffect(() => {
        let isMounted: boolean = true;

        if (canDispatch) {
            dispatch({ type: BackgroundDispatchTypes.ClearRNDServices });

            if (recommendedNotDoneEnabled && vin.length > 0 && state.pastMileagePage) {
                dispatch({ type: BackgroundDispatchTypes.SetLoadingRNDServices });
                getRNDServices(kioskKey, dealerKey, vin, mileage)
                    .then((rndServices: RNDServiceData) => {
                        isMounted && dispatch({ type: BackgroundDispatchTypes.SetRNDServices,
                            payload: rndServices });
                    })
                    .catch(() => {
                        isMounted && dispatch({ type: BackgroundDispatchTypes.SetRNDServices,
                            payload: { lastVisit: null,
                                services: [] } });
                    });
            }
            else {
                dispatch({ type: BackgroundDispatchTypes.SetRNDServices,
                    payload: { lastVisit: null,
                        services: [] } });
            }
        }

        return () => {
            isMounted = false;
        };
    }, [kioskKey, dealerKey, vin, mileage, recommendedNotDoneEnabled, state.pastMileagePage, canDispatch]);

    useEffect(() => {
        if (state.standardServicesInitialized && state.rndServicesFetched && !state.rndServicesInitialized && canDispatch) {
            const spgPricedServices: IService[] = state.standardServices.filter((s) => !!s.useSpgPricing && !!s.spgPricing);

            if (state.rndServices.length > 0 && spgPricedServices.length > 0) {
                dispatch({
                    type: BackgroundDispatchTypes.InitializeRNDServices,
                    payload: state.rndServices.map((rnd) => {
                        if (rnd.linkedService) {
                            if (rnd.price !== null && rnd.price !== undefined) {
                                // RND price data takes priority, wipe any SPG price data so it doesn't conflict when displaying price literal
                                return {
                                    ...rnd,
                                    linkedService: { ...rnd.linkedService,
                                        useSpgPricing: false,
                                        priceLow: undefined,
                                        priceHigh: undefined,
                                        spgPricing: undefined },
                                };
                            }
                            else {
                                const spgPricedService: IService | undefined = spgPricedServices.find((spg) => spg.opCode === rnd.opCode && spg.isSpgOp);

                                if (spgPricedService) {
                                    const { priceLow, priceHigh, isSpgOp } = spgPricedService;

                                    return {
                                        ...rnd,
                                        linkedService: { ...rnd.linkedService,
                                            priceLow,
                                            priceHigh,
                                            isSpgOp },
                                    };
                                }
                            }
                        }

                        return rnd;
                    }),
                });
            }
            else {
                dispatch({ type: BackgroundDispatchTypes.SetRNDServicesInitialized,
                    payload: true });
            }
        }
    }, [state.standardServicesInitialized, state.standardServices, state.rndServices, state.rndServicesFetched, state.rndServicesInitialized, canDispatch]);

    return (
        <BackgroundStateContext.Provider value={state}>
            <BackgroundDispatchContext.Provider value={dispatch}>{props.children}</BackgroundDispatchContext.Provider>
        </BackgroundStateContext.Provider>
    );
};

export const useBackgroundDataState = () => {
    const context = useContext(BackgroundStateContext);
    if (!context) {
        throw new Error('No Background Data Context');
    }

    return context;
};

export const useBackgroundDataDispatch = () => {
    const context = useContext(BackgroundDispatchContext);
    if (context === undefined) {
        throw new Error('No Background Data Dispatch Context');
    }

    return context;
};
